/**
 * Compress an image file to target size (default 2MB)
 * @param {File} file - The image file to compress
 * @param {number} maxSizeMB - Maximum size in MB (default 2)
 * @param {number} maxWidthOrHeight - Maximum width or height in pixels (default 1920)
 * @returns {Promise<File>} - Compressed file
 */
export const compressImage = (file, maxSizeMB = 2, maxWidthOrHeight = 1920) => {
  return new Promise((resolve, reject) => {
    // Only compress images, not PDFs
    if (!file.type.startsWith('image/')) {
      resolve(file);
      return;
    }

    const reader = new FileReader();
    reader.readAsDataURL(file);

    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;

      img.onload = () => {
        const canvas = document.createElement('canvas');
        let { width, height } = img;

        // Scale down if image is too large
        if (width > maxWidthOrHeight || height > maxWidthOrHeight) {
          if (width > height) {
            height = (height / width) * maxWidthOrHeight;
            width = maxWidthOrHeight;
          } else {
            width = (width / height) * maxWidthOrHeight;
            height = maxWidthOrHeight;
          }
        }

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        // Start with quality 0.9 and reduce until file is under maxSizeMB
        let quality = 0.9;
        const targetSize = maxSizeMB * 1024 * 1024;

        const tryCompress = () => {
          canvas.toBlob(
            (blob) => {
              if (blob.size > targetSize && quality > 0.1) {
                quality -= 0.1;
                tryCompress();
              } else {
                // Create a new file with the compressed blob
                const compressedFile = new File([blob], file.name, {
                  type: file.type === 'image/png' ? 'image/jpeg' : file.type,
                  lastModified: Date.now(),
                });
                resolve(compressedFile);
              }
            },
            'image/jpeg',
            quality
          );
        };

        tryCompress();
      };

      img.onerror = (error) => {
        reject(error);
      };
    };

    reader.onerror = (error) => {
      reject(error);
    };
  });
};

/**
 * Check if file needs compression and compress if necessary
 * @param {File} file - The file to check and potentially compress
 * @param {number} maxSizeMB - Maximum allowed size without compression (default 2MB)
 * @param {number} maxAllowedMB - Maximum allowed size for compression (default 10MB)
 * @returns {Promise<{file: File, wasCompressed: boolean}>}
 */
export const processFileForUpload = async (file, maxSizeMB = 2, maxAllowedMB = 10) => {
  const fileSizeMB = file.size / (1024 * 1024);

  // If file is already under limit, return as is
  if (fileSizeMB <= maxSizeMB) {
    return { file, wasCompressed: false };
  }

  // If file is over max allowed, reject
  if (fileSizeMB > maxAllowedMB) {
    throw new Error(`File size exceeds ${maxAllowedMB}MB limit`);
  }

  // If it's not an image, we can't compress it
  if (!file.type.startsWith('image/')) {
    throw new Error(`PDF files must be under ${maxSizeMB}MB`);
  }

  // Compress the image
  const compressedFile = await compressImage(file, maxSizeMB);
  return { file: compressedFile, wasCompressed: true };
};
